/**
 * Display timezone for all user-facing dates/times in the portal.
 * Uses America/New_York (Eastern Time — EST/EDT).
 */

export const DISPLAY_TIME_ZONE = "America/New_York";

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parse API / form values into a Date for Eastern display formatting. */
export function parseDisplayDate(
  input: string | Date | null | undefined,
): Date | null {
  if (input == null || input === "") return null;
  const trimmed = input instanceof Date ? "" : input.trim();
  const dateOnly = DATE_ONLY_RE.exec(trimmed);
  if (dateOnly) {
    // Calendar date without time — anchor at UTC noon so the Eastern calendar day stays stable.
    return new Date(
      Date.UTC(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
        12,
        0,
        0,
      ),
    );
  }

  const parsed = input instanceof Date ? input : new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  // Day fields (due date, event date) are stored as midnight UTC; show that calendar day.
  // shortcut: a real moment at exactly 00:00:00.000Z is shown as that day at noon; fine until such moments matter.
  if (parsed.getTime() % 86_400_000 === 0) {
    return new Date(parsed.getTime() + 12 * 3_600_000);
  }
  return parsed;
}

/** YYYY-MM-DD calendar key in Eastern Time. */
export function getDisplayDateKey(date: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date);
}

/**
 * Today in New York as a LOCAL-midnight Date, for calendar widgets and
 * date-only form state (they read local getters, so the day never shifts).
 */
export function getDisplayToday(now: Date = new Date()): Date {
  const [year, month, day] = getDisplayDateKey(now).split("-").map(Number);
  return new Date(year, month - 1, day);
}

/** A stored date/datetime as the LOCAL-midnight Date of its New York day (picker form state). */
export function toDisplayPickerDate(
  input: string | Date | null | undefined,
): Date | undefined {
  const date = parseDisplayDate(input);
  return date ? getDisplayToday(date) : undefined;
}

/** New York's UTC offset (ms, negative) at an instant. */
function displayOffsetMs(at: Date): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: DISPLAY_TIME_ZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  );
  const wall = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return wall - Math.floor(at.getTime() / 1000) * 1000;
}

/** The instant a New York calendar day (YYYY-MM-DD) starts. */
export function displayDayStart(key: string): Date {
  const utcMidnight = Date.parse(`${key}T00:00:00Z`);
  const guess = utcMidnight - displayOffsetMs(new Date(utcMidnight));
  // Re-read the offset at the guess so a DST change that day is honoured.
  return new Date(utcMidnight - displayOffsetMs(new Date(guess)));
}

/** The last millisecond of a New York calendar day (YYYY-MM-DD). */
export function displayDayEnd(key: string): Date {
  const next = new Date(Date.parse(`${key}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10);
  return new Date(displayDayStart(next).getTime() - 1);
}

/** Eastern calendar key for a stored date/datetime value. */
export function displayDateKeyFromInput(
  input: string | Date | null | undefined,
): string | null {
  const date = parseDisplayDate(input);
  if (!date) return null;
  return getDisplayDateKey(date);
}

/** Signed day delta: `later` − `earlier` in Eastern calendar days. */
export function diffDisplayCalendarDays(
  later: string | Date,
  earlier: string | Date = new Date(),
): number {
  const laterKey = displayDateKeyFromInput(later);
  const earlierKey = displayDateKeyFromInput(earlier);
  if (!laterKey || !earlierKey) return 0;

  const laterMs = Date.parse(`${laterKey}T00:00:00Z`);
  const earlierMs = Date.parse(`${earlierKey}T00:00:00Z`);
  return Math.round((laterMs - earlierMs) / 86_400_000);
}

function formatWithOptions(
  input: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions,
): string {
  const date = parseDisplayDate(input);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-US", {
    ...options,
    timeZone: DISPLAY_TIME_ZONE,
  }).format(date);
}

/** "December 10, 2025, 3:46 PM" */
export function formatDateTime(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** "December 10, 2025" */
export function formatDate(input: string | Date | null | undefined): string {
  return formatWithOptions(input, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** "Dec 10" */
export function formatDateShort(input: string | Date | null | undefined): string {
  return formatWithOptions(input, {
    month: "short",
    day: "numeric",
  });
}

/** "Dec 10, 2025" */
export function formatDateMedium(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Dec 10, 2025, 3:46 PM" */
export function formatDateTimeMedium(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** "Dec 10, 3:46 PM" */
export function formatDateTimeCompact(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/** "Wed, Dec 10, 2025" */
export function formatDateWithWeekday(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Wed, Dec 10" */
export function formatDateWeekdayShort(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** "December 2025" */
export function formatMonthYear(input: string | Date | null | undefined): string {
  return formatWithOptions(input, {
    month: "long",
    year: "numeric",
  });
}

/** "Wednesday, December 10, 2025" */
export function formatDateLongWithWeekday(
  input: string | Date | null | undefined,
): string {
  return formatWithOptions(input, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** "December" */
export function formatMonthName(input: string | Date | null | undefined): string {
  return formatWithOptions(input, { month: "long" });
}
